import {
  AUDIENCE_COMPOSITION_DEPTH_LIMIT,
  type Audience,
  type Role,
} from "@event-planner/shared";

/**
 * Why a particular consumer doc (session, travelItem, notification) targets
 * the report subject. Returned by `traceMembership` for the consumer's
 * `audienceId`.
 *
 * The shape is intentionally minimal — the UI layer renders these to chips.
 * For anonymous compositions the helper walks `members.includedAudienceIds`
 * to surface the actual reachability paths through that composition, but
 * never deeper than `AUDIENCE_COMPOSITION_DEPTH_LIMIT` to match the
 * resolver's defensive backstop.
 */
export type MembershipAttribution =
  | { kind: "personal" }
  | { kind: "role"; role: Role; audienceName: string }
  | { kind: "group"; audienceId: string; audienceName: string }
  | { kind: "anonymous"; via: MembershipAttribution[] };

/**
 * Pre-built lookup index handed to `traceMembership`. Construct once per
 * report load; the helper is pure and synchronous.
 */
export interface TraceMembershipIndex {
  /**
   * All audiences referenced by any consumer drawn in the report, plus
   * every audience referenced via `members.includedAudienceIds` from
   * those consumers. Audiences not in this map are surfaced as
   * `anonymous { via: [] }` (defensive: deleted-audience case).
   */
  audiencesById: Map<string, Audience>;
  /** Deterministic `aud_personal_${eventId}_${personId}` for the subject. */
  personalAudienceId: string;
  /** Subject's `persons.id`. ADR-136: identity is person-keyed; the
   *  Auth uid (when claimed) is incidental to audience attribution. */
  subjectPersonId: string;
  /** Subject's per-event role, or null when not on the participants roster. */
  subjectRole: Role | null;
  /**
   * Every audience the subject is reachable through in this event. Used
   * to filter composition walking so we don't attribute via paths that
   * don't actually contain the subject.
   */
  subjectAudienceIds: Set<string>;
}

/**
 * Classify why a consumer doc targeting `consumerAudienceId` reaches the
 * subject described by `idx`. Pure / synchronous / no IO.
 *
 * Decision order (first match wins):
 *   1. consumerAudienceId === idx.personalAudienceId → personal
 *   2. audience missing from idx → anonymous { via: [] }   (defensive)
 *   3. audience.system && audience.name → role
 *   4. !audience.system && audience.name → group
 *   5. anonymous (no name, not system) → walk members.{personIds, roles,
 *      includedAudienceIds} and classify each match recursively, capped
 *      at AUDIENCE_COMPOSITION_DEPTH_LIMIT.
 *
 * A system-but-unnamed audience (someone else's personal audience) falls
 * to anonymous{via:[]} defensively — the report shouldn't be drawing the
 * row at all in that case, because the subject isn't in that personal
 * audience.
 */
export function traceMembership(
  consumerAudienceId: string,
  idx: TraceMembershipIndex,
): MembershipAttribution {
  return traceImpl(consumerAudienceId, idx, AUDIENCE_COMPOSITION_DEPTH_LIMIT);
}

function traceImpl(
  audienceId: string,
  idx: TraceMembershipIndex,
  depthBudget: number,
): MembershipAttribution {
  if (audienceId === idx.personalAudienceId) {
    return { kind: "personal" };
  }

  const audience = idx.audiencesById.get(audienceId);
  if (!audience) {
    return { kind: "anonymous", via: [] };
  }

  if (audience.system && audience.name) {
    const role = audience.members.roles[0];
    if (role) {
      return { kind: "role", role, audienceName: audience.name };
    }
    // 0 or >1 roles in a role audience is data corruption — fall through.
  }

  if (!audience.system && audience.name) {
    return { kind: "group", audienceId, audienceName: audience.name };
  }

  // System-but-unnamed = a personal audience for some other person.
  // The subject isn't in it; defensive fallthrough.
  if (audience.system && !audience.name) {
    return { kind: "anonymous", via: [] };
  }

  // Anonymous composition. Walk the members spec.
  if (depthBudget <= 0) {
    return { kind: "anonymous", via: [] };
  }

  const via: MembershipAttribution[] = [];

  if (audience.members.personIds.includes(idx.subjectPersonId)) {
    via.push({ kind: "personal" });
  }

  if (
    idx.subjectRole &&
    audience.members.roles.includes(idx.subjectRole)
  ) {
    via.push({
      kind: "role",
      role: idx.subjectRole,
      audienceName: roleAudienceName(idx, idx.subjectRole),
    });
  }

  for (const includedId of audience.members.includedAudienceIds) {
    if (!isSubjectReachableVia(includedId, idx)) continue;
    via.push(traceImpl(includedId, idx, depthBudget - 1));
  }

  return { kind: "anonymous", via };
}

/**
 * Best-effort role-audience name lookup. Falls back to a generic label
 * if the role audience isn't in the index. Role audiences are bootstrapped
 * per event and small in number, so the linear scan is fine.
 */
function roleAudienceName(idx: TraceMembershipIndex, role: Role): string {
  for (const audience of idx.audiencesById.values()) {
    if (
      audience.system &&
      audience.name &&
      audience.members.roles.length === 1 &&
      audience.members.roles[0] === role
    ) {
      return audience.name;
    }
  }
  return `All ${role}s`;
}

function isSubjectReachableVia(
  audienceId: string,
  idx: TraceMembershipIndex,
): boolean {
  if (idx.subjectAudienceIds.has(audienceId)) return true;
  const audience = idx.audiencesById.get(audienceId);
  if (!audience) return false;
  return (audience.resolvedPersonIds ?? []).includes(idx.subjectPersonId);
}
